import { Badge, Card } from "@contractor-quoting/shared-ui";

export interface ContractorSpace {
  id: string;
  name: string;
  kind?: string | null;
  shape?: string | null;
  remove_existing_flooring?: boolean;
  requires_stair_review?: boolean;
}

export interface ContractorMeasurement {
  id: string;
  project_space_id: string;
  kind: string;
  label?: string | null;
  width_mm: number | string;
  length_mm: number | string;
}

function formatLength(value: number | string) {
  const millimetres = Number(value);
  if (!Number.isFinite(millimetres)) return "Unknown";

  const totalTenthsOfAnInch = Math.round((millimetres / 25.4) * 10);
  const feet = Math.floor(totalTenthsOfAnInch / 120);
  const inches = (totalTenthsOfAnInch - feet * 120) / 10;
  return `${feet}′ ${inches.toFixed(inches % 1 === 0 ? 0 : 1)}″`;
}

function formatArea(width: number | string, length: number | string) {
  const squareFeet = (Number(width) * Number(length)) / 92_903.04;
  return Number.isFinite(squareFeet) ? `${squareFeet.toFixed(1)} sq ft` : "Area unavailable";
}

function humanize(value: string) {
  return value.replaceAll("_", " ");
}

export function ContractorMeasurements({
  spaces,
  measurements,
}: {
  spaces: ContractorSpace[];
  measurements: ContractorMeasurement[];
}) {
  return (
    <Card className="lg:col-span-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xl font-bold">Measurements</h2>
        <p className="text-sm text-stone-500">
          {measurements.length} measurement {measurements.length === 1 ? "record" : "records"}
        </p>
      </div>
      {spaces.length === 0 ? (
        <p className="mt-3 text-stone-600">No measurements have been recorded.</p>
      ) : (
        <div className="mt-4 grid gap-4">
          {spaces.map((space) => {
            const spaceMeasurements = measurements.filter(
              (measurement) => measurement.project_space_id === space.id,
            );
            return (
              <section
                className="rounded-xl border border-stone-200 bg-stone-50 p-4"
                key={space.id}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-black text-emerald-950">{space.name}</h3>
                    <p className="mt-1 text-sm capitalize text-stone-500">
                      {[space.kind, space.shape]
                        .filter((value): value is string => Boolean(value))
                        .map(humanize)
                        .join(" · ")}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {space.remove_existing_flooring ? <Badge>Flooring removal</Badge> : null}
                    {space.requires_stair_review ? <Badge>Stair review</Badge> : null}
                  </div>
                </div>
                {spaceMeasurements.length === 0 ? (
                  <p className="mt-3 text-sm text-stone-500">
                    No dimensions recorded for this space.
                  </p>
                ) : (
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {spaceMeasurements.map((measurement) => (
                      <div
                        className="rounded-lg border border-stone-200 bg-white p-3"
                        key={measurement.id}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-bold">
                            {measurement.label || humanize(measurement.kind)}
                          </p>
                          <span
                            className={`rounded-full px-2 py-1 text-xs font-bold capitalize ${
                              measurement.kind === "exclusion"
                                ? "bg-amber-100 text-amber-900"
                                : "bg-emerald-100 text-emerald-900"
                            }`}
                          >
                            {measurement.kind === "exclusion"
                              ? "Excluded area"
                              : humanize(measurement.kind)}
                          </span>
                        </div>
                        <p className="mt-2 text-stone-700">
                          {formatLength(measurement.width_mm)} ×{" "}
                          {formatLength(measurement.length_mm)}
                        </p>
                        <p className="mt-1 text-sm text-stone-500">
                          {formatArea(measurement.width_mm, measurement.length_mm)} ·{" "}
                          {measurement.width_mm} × {measurement.length_mm} mm
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </Card>
  );
}
